import { useQuery } from '@apollo/client';
import { Outlet, useOutletContext, useParams } from 'react-router';
import invariant from 'tiny-invariant';
import { graphql } from '~/apollo/generated/v3';
import type { UploadLtUpdatePageQuery } from '~/apollo/generated/v3/graphql';
import { NotFound } from '~/components/common/NotFound';
import { PageHeading } from '~/components/common/PageHeading';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { useBreadcrumb } from '~/components/layout/Breadcrumb';
import { NavTabs } from '~/components/navigation/NavTabs';
import * as routes from '~/paths';
import type { RefetchQueries } from '~/utils/graphql';
import { createRefetchQueries } from '~/utils/graphql';

const UPLOAD_LT_UPDATE_PAGE = graphql(`
  query UploadLTUpdatePage($id: Int!) {
    lithostratTypeList(id: $id) {
      ...lithostratTypeParts
    }
  }
`);

type OutletContext = {
  lithostratType: UploadLtUpdatePageQuery['lithostratTypeList'][number];
  refetchQueries: RefetchQueries;
};

export default function UploadLithostratTypeUpdateRoute() {
  const params = useParams<{ lithostratTypeId: string }>();
  invariant(params.lithostratTypeId, 'lithostratTypeId param required');
  const ltId = parseInt(params.lithostratTypeId);

  const { data, loading } = useQuery(UPLOAD_LT_UPDATE_PAGE, {
    variables: { id: ltId },
  });

  const refetchQueries = createRefetchQueries([
    {
      query: UPLOAD_LT_UPDATE_PAGE,
      variables: { id: ltId },
    },
  ]);

  const lt = data?.lithostratTypeList.find(lt => lt.id === ltId);

  useBreadcrumb(
    'routes/upload/lithostrat-type/$lithostratTypeId',
    lt?.name ?? 'Update Lithostrat Type',
  );

  if (loading) return <SpinnerPlaceholder />;
  if (!lt) return <NotFound />;

  const outletContext: OutletContext = {
    lithostratType: lt,
    refetchQueries,
  };

  return (
    <>
      <PageHeading hasSubtitle={!!lt}>Update Lithostrat type</PageHeading>
      {lt && <PageHeading.Subtitle>{lt.name}</PageHeading.Subtitle>}

      <NavTabs className="mb-2">
        <NavTabs.Tab to={routes.uploadLithostratTypeUpdateRoute(lt.id)} end>
          Details
        </NavTabs.Tab>
        <NavTabs.Tab
          to={routes.uploadLithostratTypeUpdateDiagramTabRoute(lt.id)}
        >
          Diagram
        </NavTabs.Tab>
        <NavTabs.Tab
          to={routes.uploadLithostratTypeUpdateEntitiesTabRoute(lt.id)}
        >
          Entities
        </NavTabs.Tab>
      </NavTabs>

      <Outlet context={outletContext} />
    </>
  );
}

export function useLithostratTypeUpdateOutletContext() {
  return useOutletContext<OutletContext>();
}
